import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { ShopifyConnect } from "../components/ShopifyConnect";
import { BrandVerifyBanner } from "../components/BrandVerifyBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Megaphone, Users, Handshake, ClipboardCheck, MessageSquare, TrendingUp, ArrowRight,
  Plus, Compass, Sparkles, Bell, Download, Building2, ExternalLink, Trash2, PenSquare, Package, Check, Star,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, BarChart, Bar } from "recharts";

const CHART = ["#FF3B6B", "#9D4EDD", "#2EC5FF", "#66D19E", "#FFB020"];
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function StatCard({ label, value, hint, icon: Icon, tone = "default", testid }) {
  const map = { default: "text-white", up: "text-emerald-300", warn: "text-yellow-300", hot: "text-nexus-pink" };
  return (
    <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      </div>
      <div className={`font-display font-bold text-2xl tabular-nums ${map[tone] || map.default}`}>{value}</div>
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

/* ================= BRAND DASHBOARD ================= */
export function BrandDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "brand") { navigate("/creator"); return; }
    api.get("/brand/dashboard-stats").then((r) => setData(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  const s = data?.stats || {};
  const statusData = Object.entries(data?.campaign_status || {}).map(([name, value]) => ({ name, value }));

  return (
    <BrandShell title="Dashboard">
      <SEO title="Brand Dashboard · Nexus" description="Manage your creator marketing campaigns." path="/brand" />
      <div className="p-6 lg:p-10 space-y-8">
        <BrandVerifyBanner />
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Welcome back</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
              Hey {user?.name?.split(" ")[0] || "there"} — <span className="nexus-gradient-text">let&apos;s ship a campaign.</span>
            </h1>
          </div>
          <Button onClick={() => navigate("/campaigns/new")} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 h-11" data-testid="dashboard-create-campaign">
            <Plus className="h-4 w-4 mr-2" /> Create campaign
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="dashboard-stats">
          <StatCard label="Active campaigns" value={s.active_campaigns ?? 0} icon={Megaphone} testid="stat-active" />
          <StatCard label="Applications" value={s.applications_received ?? 0} icon={Handshake} tone="up" testid="stat-applications" />
          <StatCard label="Creators hired" value={s.creators_hired ?? 0} icon={Users} testid="stat-hired" />
          <StatCard label="Approval queue" value={s.approval_queue ?? 0} icon={ClipboardCheck} tone="warn" testid="stat-approval" />
          <StatCard label="Messages" value={s.unread_messages ?? 0} icon={MessageSquare} tone={(s.unread_messages || 0) > 0 ? "hot" : "default"} testid="stat-messages" />
          <StatCard label="Total spend" value={`£${(s.total_spend || 0).toFixed(0)}`} icon={TrendingUp} testid="stat-spend" />
          <StatCard label="Campaign ROI" value={`${s.roi_pct || 0}%`} icon={TrendingUp} tone="up" testid="stat-roi" />
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Recent activity</div>
              <Link to="/brand/applications" className="text-xs text-nexus-blue hover:text-white">View all →</Link>
            </div>
            <div className="space-y-3" data-testid="dashboard-activity">
              {(data?.activity || []).length === 0 && <p className="text-sm text-zinc-500">Nothing to show yet — post a campaign to get the ball rolling.</p>}
              {(data?.activity || []).map((a, i) => (
                <Link key={i} to={a.link} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                  <div className="h-9 w-9 rounded-full nexus-gradient flex items-center justify-center shrink-0">
                    {a.type === "application" ? <Handshake className="h-4 w-4 text-white" /> : a.type === "content_submitted" ? <ClipboardCheck className="h-4 w-4 text-white" /> : <TrendingUp className="h-4 w-4 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{a.text}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{timeAgo(a.when)}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-zinc-500 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Campaign status</div>
              {statusData.length === 0 ? (
                <p className="text-sm text-zinc-500">No campaigns yet.</p>
              ) : (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={65} paddingAngle={2}>
                        {statusData.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="flex justify-center gap-3 flex-wrap text-xs mt-2">
                {statusData.map((d, i) => <div key={d.name} className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: CHART[i % CHART.length] }} />{d.name} · {d.value}</div>)}
              </div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Quick actions</div>
              <div className="space-y-2">
                {[
                  { to: "/campaigns/new", label: "Create campaign", icon: Plus },
                  { to: "/discover", label: "Browse creators", icon: Compass },
                  { to: "/brand/applications", label: "View applications", icon: Handshake },
                  { to: "/messages", label: "Open messages", icon: MessageSquare },
                ].map((a) => {
                  const Icon = a.icon;
                  return (
                    <Link key={a.to} to={a.to} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-sm">
                      <Icon className="h-4 w-4 text-nexus-blue" /> {a.label}
                      <ArrowRight className="h-3.5 w-3.5 ml-auto text-zinc-500" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= BRAND APPLICATIONS (aggregate) ================= */
export function BrandApplications() {
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const cs = await api.get("/campaigns?mine=1");
      const all = [];
      for (const c of cs.data) {
        const r = await api.get(`/campaigns/${c.id}`);
        for (const a of r.data.applications || []) all.push({ ...a, campaign_title: c.title, campaign_id: c.id });
      }
      setRows(all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clear = () => setChecked(new Set());

  // Group checked by campaign (backend requires per-campaign call)
  const byCampaign = () => {
    const grouped = {};
    for (const r of rows) if (checked.has(r.id)) (grouped[r.campaign_id] ||= []).push(r.id);
    return grouped;
  };

  const shortlist = async () => {
    if (checked.size === 0) return;
    setBusy(true);
    let total = 0;
    try {
      for (const [campaign_id, application_ids] of Object.entries(byCampaign())) {
        const r = await api.post("/brand/applications/shortlist", { campaign_id, application_ids, message: note });
        total += r.data.count || 0;
      }
      toast.success(`Shortlisted ${total} · creators notified`);
      clear(); setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <BrandShell title="Applications">
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <h1 className="font-display font-bold text-3xl tracking-tighter">Applications</h1>
          {rows.length > 0 && (
            <div className="text-xs uppercase tracking-widest text-zinc-500">{rows.length} total · {rows.filter((r) => r.shortlisted).length} shortlisted</div>
          )}
        </div>

        {checked.size > 0 && (
          <div className="glass rounded-2xl p-4 flex items-center gap-3 flex-wrap sticky top-4 z-30 nexus-border-glow" data-testid="applications-bulk-bar">
            <span className="text-xs uppercase tracking-widest text-white">{checked.size} selected</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: note to shortlisted creators" className="flex-1 min-w-[200px] bg-white/[0.02] border border-white/10 rounded-full px-4 py-1.5 text-sm text-white placeholder:text-zinc-600" data-testid="applications-shortlist-note" />
            <Button onClick={shortlist} disabled={busy} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="applications-shortlist-btn">
              <Star className="h-4 w-4 mr-1.5" /> Shortlist selected
            </Button>
            <button onClick={clear} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="applications-clear-btn">Clear</button>
          </div>
        )}

        {rows.length === 0 && <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">No applications yet — post a campaign.</div>}
        <div className="space-y-3">
          {rows.map((a) => {
            const isChecked = checked.has(a.id);
            return (
              <div key={a.id} className={`glass rounded-2xl p-5 flex items-center gap-4 flex-wrap transition-colors ${isChecked ? "nexus-border-glow" : ""}`} data-testid={`app-${a.id}`}>
                <button type="button" onClick={() => toggle(a.id)} className={`h-4 w-4 rounded border shrink-0 flex items-center justify-center transition-colors ${isChecked ? "bg-nexus-purple border-nexus-purple" : "border-white/20 hover:border-white/40"}`} data-testid={`app-check-${a.id}`} aria-label="select">
                  {isChecked && <Check className="h-3 w-3 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg truncate">{a.creator_name}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${a.status === "accepted" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : a.status === "rejected" ? "border-nexus-pink/40 bg-nexus-pink/10 text-nexus-pink" : "border-nexus-blue/40 bg-nexus-blue/10 text-nexus-blue"}`}>{a.status}</span>
                    {a.mode === "bid" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-nexus-purple/40 bg-nexus-purple/10 text-nexus-purple">bid</span>}
                    {a.shortlisted && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 inline-flex items-center gap-1"><Star className="h-2.5 w-2.5" /> Shortlisted</span>}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Campaign · {a.campaign_title}</div>
                  {a.pitch && <p className="text-sm text-zinc-300 mt-2 line-clamp-2">{a.pitch}</p>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Offer</div>
                  <div className="font-display font-bold text-2xl nexus-gradient-text tabular-nums">£{a.quoted_price}</div>
                </div>
                <Link to={`/campaigns/${a.campaign_id}`} className="text-xs uppercase tracking-widest text-nexus-blue hover:text-white inline-flex items-center gap-1">
                  Open <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= BRAND ORDERS (hires) ================= */
export function BrandOrders() {
  const [hires, setHires] = useState([]);
  useEffect(() => { api.get("/hires").then((r) => setHires(r.data)).catch(() => {}); }, []);
  return (
    <BrandShell title="Orders">
      <div className="p-6 lg:p-10 space-y-6">
        <h1 className="font-display font-bold text-3xl tracking-tighter">Orders</h1>
        {hires.length === 0 && <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">No orders yet.</div>}
        <div className="space-y-3">
          {hires.map((h) => (
            <Link key={h.id} to={`/deals/${h.id}`} className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-white/20 transition-colors" data-testid={`order-${h.id}`}>
              <Package className="h-5 w-5 text-nexus-pink" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate">{(h.package || {}).title || "Collab"}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{h.counterparty_name} · {h.status.replace(/_/g, " ")}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Amount</div>
                <div className="font-display font-bold text-lg tabular-nums">£{(h.brand_total || 0).toFixed(0)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= BRAND ANALYTICS ================= */
export function BrandAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/brand/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  const exportCsv = () => {
    if (!data) return;
    const rows = [["Campaign", "Status", "Applied", "Hired", "Spend (£)", "Est. reach", "CPM (£)"]];
    for (const c of data.campaigns) rows.push([c.title, c.status, c.applied, c.hired, c.spend, c.est_reach, c.cpm]);
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `nexus-analytics-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };
  if (!data) return <BrandShell title="Analytics"><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></BrandShell>;
  return (
    <BrandShell title="Analytics">
      <div className="p-6 lg:p-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Analytics</div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Every campaign, <span className="nexus-gradient-text">measured.</span></h1>
          </div>
          <Button onClick={exportCsv} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="analytics-export-csv">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard label="Total campaigns" value={data.totals.campaigns} testid="analytics-total-campaigns" />
          <StatCard label="Total spend" value={`£${data.totals.spend.toFixed(0)}`} testid="analytics-total-spend" />
          <StatCard label="Est. reach" value={data.totals.reach >= 1000 ? `${(data.totals.reach / 1000).toFixed(1)}K` : data.totals.reach} testid="analytics-total-reach" />
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Spend (last 30 days)</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.spend_series}>
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={40} />
                <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Line type="monotone" dataKey="spend" stroke="#9D4EDD" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Campaign performance</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                <tr><th className="text-left p-2">Campaign</th><th className="text-left p-2">Status</th><th className="text-right p-2">Applied</th><th className="text-right p-2">Hired</th><th className="text-right p-2">Spend</th><th className="text-right p-2">Reach</th><th className="text-right p-2">CPM</th></tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-t border-white/5">
                    <td className="p-2 font-semibold truncate max-w-[200px]">{c.title}</td>
                    <td className="p-2 text-xs uppercase tracking-widest text-zinc-400">{c.status}</td>
                    <td className="p-2 text-right tabular-nums">{c.applied}</td>
                    <td className="p-2 text-right tabular-nums">{c.hired}</td>
                    <td className="p-2 text-right tabular-nums">£{c.spend.toFixed(0)}</td>
                    <td className="p-2 text-right tabular-nums">{c.est_reach >= 1000 ? `${(c.est_reach / 1000).toFixed(1)}K` : c.est_reach}</td>
                    <td className="p-2 text-right tabular-nums text-nexus-blue">£{c.cpm.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= SAVED CREATORS ================= */
export function BrandSaved() {
  const [data, setData] = useState({ collections: {}, total: 0 });
  const [newName, setNewName] = useState("");
  const load = () => api.get("/brand/saved-creators").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const move = async (creator_id, collection) => {
    try { await api.patch(`/brand/saved-creators/${creator_id}`, { collection }); await load(); }
    catch (e) { toast.error("Move failed"); }
  };
  const remove = async (creator_id) => {
    try { await api.delete(`/brand/saved-creators/${creator_id}`); await load(); toast.success("Removed"); }
    catch (e) { toast.error("Remove failed"); }
  };
  const collections = Object.keys(data.collections);
  return (
    <BrandShell title="Saved Creators">
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Saved Creators</div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">{data.total} saved · {collections.length} {collections.length === 1 ? "collection" : "collections"}</h1>
          </div>
          <Link to="/discover" className="text-sm text-nexus-blue hover:text-white inline-flex items-center gap-1"><Compass className="h-4 w-4" /> Discover more</Link>
        </div>
        {collections.length === 0 && <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500">Save creators from Discover to build collections.</div>}
        <div className="space-y-8" data-testid="saved-collections">
          {collections.map((coll) => (
            <div key={coll}>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-3">{coll} · {data.collections[coll].length}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.collections[coll].map((s) => (
                  <div key={s.creator_id} className="glass rounded-2xl p-4 flex gap-3" data-testid={`saved-${s.creator_id}`}>
                    <div className="h-14 w-14 rounded-xl overflow-hidden bg-white/5 hairline shrink-0">
                      {s.creator.avatar_url && <img src={s.creator.avatar_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold truncate">{s.creator.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{s.creator.tagline}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{(s.creator.follower_count || 0).toLocaleString()} followers</div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Link to={`/creators/${s.creator_id}`} className="text-[10px] uppercase tracking-widest text-nexus-blue hover:text-white">View →</Link>
                        <select value={coll} onChange={(e) => move(s.creator_id, e.target.value)} className="text-[10px] uppercase tracking-widest bg-transparent border border-white/10 rounded-full px-2 py-0.5 text-zinc-300 hover:border-white/25">
                          {[...new Set([...collections, "Favourites"])].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button onClick={() => remove(s.creator_id)} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-nexus-pink ml-auto"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Create a new collection</div>
          <div className="flex gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Christmas Campaign" className="bg-white/[0.02] border-white/10 text-white" data-testid="new-collection-name" />
            <Button onClick={() => { setData((d) => ({ ...d, collections: { ...d.collections, [newName || "New collection"]: [] } })); setNewName(""); }} disabled={!newName.trim()} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90">
              <Plus className="h-4 w-4 mr-2" /> Add
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mt-2">Tip: drop creators into a collection via the dropdown on each card.</p>
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= PAYMENT CENTRE ================= */
export function BrandPayments() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  useEffect(() => { api.get("/brand/payments").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <BrandShell title="Payments"><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></BrandShell>;
  const t = data.totals || {};
  const filtered = (data.invoices || []).filter((i) =>
    tab === "all" ? true : tab === "in_flight" ? ["pending", "in_progress", "delivered"].includes(i.status)
      : tab === "released" ? ["released", "partially_released"].includes(i.status)
      : ["refunded", "cancelled"].includes(i.status)
  );

  const exportCsv = () => {
    const rows = [["Receipt", "Creator", "Package", "Status", "Brand total (£)", "Platform fee (£)", "Creator payout (£)", "Created"]];
    for (const i of data.invoices) rows.push([i.receipt_id, i.creator_name, i.package_title, i.status, i.brand_total, i.platform_fee, i.creator_payout, i.created_at || ""]);
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `nexus-payments-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const TABS = [
    { k: "all", l: "All" },
    { k: "in_flight", l: "In flight" },
    { k: "released", l: "Released" },
    { k: "refunded", l: "Refunded" },
  ];

  return (
    <BrandShell title="Payments">
      <SEO title="Payments · Nexus" description="Every escrow-protected transaction, one place." path="/brand/payments" />
      <div className="p-6 lg:p-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Payment centre</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Every payment <span className="nexus-gradient-text">accounted for.</span></h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Escrow held, released, refunded. Export any time.</p>
          </div>
          <Button onClick={exportCsv} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="payments-export-csv">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="payments-kpis">
          <StatCard label="Escrow held" value={`£${t.escrow_held?.toLocaleString() || 0}`} tone="warn" testid="kpi-escrow" />
          <StatCard label="Lifetime spend" value={`£${t.lifetime_spend?.toLocaleString() || 0}`} tone="up" testid="kpi-spend" />
          <StatCard label="Upcoming payouts" value={`£${t.upcoming_payouts?.toLocaleString() || 0}`} testid="kpi-upcoming" />
          <StatCard label="Refunds" value={`£${t.refunds?.toLocaleString() || 0}`} testid="kpi-refunds" />
          <StatCard label="Platform fees" value={`£${t.platform_fees_paid?.toLocaleString() || 0}`} testid="kpi-fees" />
        </div>

        {/* Monthly bar */}
        <div className="glass rounded-2xl p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Spend · last 6 months</div>
          <div className="h-56 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={data.spend_series || []}>
                <XAxis dataKey="month" tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#71717A", fontSize: 10 }} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Bar dataKey="spend" fill="#9D4EDD" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Invoices */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Invoices · {filtered.length} of {data.invoices?.length || 0}</div>
            <div className="flex gap-1.5 flex-wrap">
              {TABS.map((x) => (
                <button key={x.k} onClick={() => setTab(x.k)} data-testid={`payments-tab-${x.k}`} className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest border transition-colors ${tab === x.k ? "border-nexus-purple bg-nexus-purple/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{x.l}</button>
              ))}
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Nothing here yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left p-2">Receipt</th>
                    <th className="text-left p-2">Creator</th>
                    <th className="text-left p-2">Package</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2">Brand total</th>
                    <th className="text-right p-2">Payout</th>
                    <th className="text-right p-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="payments-invoices">
                  {filtered.map((i) => (
                    <tr key={i.id} className="border-t border-white/5">
                      <td className="p-2 font-mono text-[11px] text-zinc-400">{i.receipt_id}</td>
                      <td className="p-2 truncate max-w-[160px]">{i.creator_name}</td>
                      <td className="p-2 truncate max-w-[220px]">{i.package_title}</td>
                      <td className="p-2 text-[10px] uppercase tracking-widest text-zinc-400">{i.status.replace(/_/g, " ")}</td>
                      <td className="p-2 text-right tabular-nums">£{Number(i.brand_total).toFixed(0)}</td>
                      <td className="p-2 text-right tabular-nums text-nexus-blue">£{Number(i.creator_payout).toFixed(0)}</td>
                      <td className="p-2 text-right">
                        <Link to={`/deals/${i.id}`} className="text-[10px] uppercase tracking-widest text-nexus-blue hover:text-white">Open →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= CONTENT APPROVAL CENTRE ================= */
export function BrandApprovals() {
  const [hires, setHires] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(new Set());

  const load = async () => {
    try {
      const r = await api.get("/hires");
      const delivered = (r.data || []).filter((h) => h.status === "delivered");
      setHires(delivered);
      setChecked(new Set());
      setSelected((prev) => prev && delivered.find((h) => h.id === prev.id) ? delivered.find((h) => h.id === prev.id) : delivered[0] || null);
    } catch (e) { toast.error("Failed to load approvals"); }
  };
  useEffect(() => { load(); }, []);

  const toggleCheck = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setChecked((s) => s.size === hires.length ? new Set() : new Set(hires.map((h) => h.id)));

  const bulkApprove = async () => {
    if (checked.size === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/brand/approvals/bulk-approve", { hire_ids: [...checked] });
      toast.success(`Approved ${r.data.approved_count} · payments released`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approve = async (hire_id) => {
    setBusy(true);
    try {
      await api.post(`/hires/${hire_id}/approve`);
      toast.success("Delivery approved — payment released");
      setSelected(null); setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const requestRevision = async (hire_id) => {
    if (!note.trim()) { toast.error("Add a note explaining what you'd like changed"); return; }
    setBusy(true);
    try {
      await api.post(`/hires/${hire_id}/request-revision`, { note });
      toast.success("Revision requested");
      setSelected(null); setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <BrandShell title="Approvals">
      <SEO title="Approvals · Nexus" description="Review and approve creator deliveries." path="/brand/approvals" />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Content Approval Centre</div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">{hires.length} {hires.length === 1 ? "delivery" : "deliveries"} waiting on you</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Approve to release payment. Request a revision if it needs work.</p>
          </div>
          {hires.length > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={toggleAll} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="approvals-toggle-all">
                {checked.size === hires.length ? "Clear" : "Select all"}
              </button>
              <Button onClick={bulkApprove} disabled={busy || checked.size === 0} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 disabled:opacity-40" data-testid="approvals-bulk-approve">
                Approve {checked.size > 0 ? `${checked.size} ` : ""}selected
              </Button>
            </div>
          )}
        </div>

        {hires.length === 0 && (
          <div className="glass rounded-2xl p-10 text-center text-sm text-zinc-500" data-testid="approvals-empty">Nothing to review yet — creators will queue up here as they submit deliveries.</div>
        )}

        {hires.length > 0 && (
          <div className="grid lg:grid-cols-[380px_1fr] gap-6">
            {/* List */}
            <div className="space-y-2" data-testid="approvals-list">
              {hires.map((h) => {
                const active = selected?.id === h.id;
                const isChecked = checked.has(h.id);
                return (
                  <div
                    key={h.id}
                    className={`w-full text-left p-4 rounded-2xl border transition-colors flex gap-3 ${active ? "border-nexus-purple bg-nexus-purple/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                    data-testid={`approval-item-${h.id}`}
                  >
                    <button type="button" onClick={() => toggleCheck(h.id)} className={`mt-1 h-4 w-4 rounded border shrink-0 flex items-center justify-center transition-colors ${isChecked ? "bg-nexus-purple border-nexus-purple" : "border-white/20 hover:border-white/40"}`} data-testid={`approval-check-${h.id}`} aria-label="select">
                      {isChecked && <Check className="h-3 w-3 text-white" />}
                    </button>
                    <button type="button" onClick={() => { setSelected(h); setNote(""); }} className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-display font-bold text-sm truncate">{(h.package || {}).title || "Collab"}</div>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 shrink-0">Delivered</span>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1 truncate">{h.counterparty_name}</div>
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
                        <span>{timeAgo(h.delivered_at)}</span>
                        <span className="text-white font-display tabular-nums">£{(h.brand_total || h.amount || 0).toFixed(0)}</span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Detail */}
            {selected && (
              <div className="glass rounded-2xl p-6 space-y-5" data-testid="approval-detail">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Package</div>
                    <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight">{(selected.package || {}).title || "Collab"}</h2>
                    <div className="text-sm text-zinc-400 mt-1">By {selected.counterparty_name}</div>
                  </div>
                  <Link to={`/deals/${selected.id}`} className="text-xs uppercase tracking-widest text-nexus-blue hover:text-white flex items-center gap-1">Open deal room <ArrowRight className="h-3 w-3" /></Link>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Amount</div>
                    <div className="font-display font-bold text-lg tabular-nums" data-testid="approval-amount">£{(selected.brand_total || selected.amount || 0).toFixed(0)}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Revisions</div>
                    <div className="font-display font-bold text-lg tabular-nums">{selected.revision_count || 0}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Delivered</div>
                    <div className="font-display font-bold text-sm">{timeAgo(selected.delivered_at)}</div>
                  </div>
                </div>

                {selected.delivery_note && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Creator note</div>
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap p-3 rounded-xl bg-white/[0.02] hairline">{selected.delivery_note}</p>
                  </div>
                )}

                {selected.deliverable_url && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Deliverable</div>
                    <a href={selected.deliverable_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/15 text-sm text-zinc-200 hover:bg-white/10 hover:text-white transition-colors" data-testid="approval-deliverable-link">
                      <ExternalLink className="h-3.5 w-3.5" /> Open deliverable
                    </a>
                  </div>
                )}

                <div className="pt-2 border-t border-white/5">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Revision note <span className="normal-case tracking-normal text-[10px] text-zinc-500">— only needed if requesting changes</span></Label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What needs to change? Be specific." className="mt-1.5 w-full bg-white/[0.02] border border-white/10 rounded-xl text-white text-sm p-3 focus:outline-none focus:border-white/25" data-testid="approval-revision-note" />
                </div>

                <div className="flex flex-wrap gap-2 justify-end">
                  <Button onClick={() => requestRevision(selected.id)} disabled={busy} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="approval-revision-btn">
                    Request revision
                  </Button>
                  <Button onClick={() => approve(selected.id)} disabled={busy} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="approval-approve-btn">
                    {busy ? "Working…" : "Approve & release payment"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </BrandShell>
  );
}

/* ================= BILLING & PLANS ================= */
const PLANS = [
  { key: "free", tier: "free", name: "Free", price: "£0", cycle: "/mo", perks: ["Up to 2 active campaigns", "20% platform fee", "Standard support"] },
  { key: "brand_starter_monthly", tier: "starter", name: "Starter", price: "£29", cycle: "/mo", perks: ["Unlimited campaigns", "15% platform fee", "Priority email support"], best: true },
  { key: "brand_pro_monthly", tier: "pro", name: "Pro", price: "£99", cycle: "/mo", perks: ["Everything in Starter", "10% platform fee", "AI Brief Assist unlimited", "Dedicated success manager"] },
  { key: "brand_founding_annual", tier: "founding", name: "Founding", price: "£249", cycle: "/yr", perks: ["Everything in Pro", "10% platform fee — locked for life", "Founding badge on your profile", "Early access to new features"], founding: true },
];

export function BrandBilling() {
  const [sub, setSub] = useState(null);
  const [ref, setRef] = useState(null);
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadRef = () => api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/subscriptions/me").then((r) => setSub(r.data)).catch(() => setSub({ tier: "free", status: "inactive" }));
    loadRef();
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, []);

  const subscribe = async (lookup_key) => {
    if (lookup_key === "free") return;
    setBusy(lookup_key);
    try {
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(null); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/payments/portal", { return_url: `${window.location.origin}/brand/billing` });
      window.location.href = r.data.portal_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not open portal"); setPortalBusy(false); }
  };

  const copyRefLink = () => {
    if (!ref) return;
    const link = `${window.location.origin}/auth?mode=signup&ref=${encodeURIComponent(ref.code)}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Referral link copied");
  };

  const redeem = async () => {
    if (redeemBusy) return;
    setRedeemBusy(true);
    try {
      const r = await api.post("/referrals/redeem");
      toast.success(`£${r.data.credit_applied} credit applied to your next invoice`);
      await loadRef();
    } catch (e) {
      const raw = formatApiError(e.response?.data?.detail) || "";
      const friendly = raw.startsWith("Stripe:")
        ? "We couldn't apply the credit right now — please try again in a moment."
        : raw || "Redeem failed";
      toast.error(friendly);
    } finally { setRedeemBusy(false); }
  };

  const currentTier = sub?.tier || "free";
  const hasActive = !!sub?.stripe_subscription_id;

  return (
    <BrandShell title="Billing">
      <SEO title="Billing · Nexus" description="Manage your Nexus subscription." path="/brand/billing" />
      <div className="p-6 lg:p-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Billing</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
              You&apos;re on <span className="nexus-gradient-text">{currentTier}</span> · {sub?.status || "inactive"}
            </h1>
            <p className="text-zinc-400 mt-2 text-sm">Upgrade to unlock unlimited campaigns and drop the platform fee.</p>
          </div>
          {hasActive && (
            <Button onClick={openPortal} disabled={portalBusy} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="billing-manage-btn">
              {portalBusy ? "Opening…" : "Manage subscription"}
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-plans">
          {PLANS.map((p) => {
            const isCurrent = currentTier === p.tier;
            const isFree = p.key === "free";
            return (
              <div
                key={p.key}
                className={`rounded-2xl p-6 border relative flex flex-col ${
                  p.founding ? "border-yellow-500/50 bg-gradient-to-br from-yellow-500/[0.08] to-nexus-pink/[0.06]"
                  : p.best ? "border-nexus-purple bg-nexus-purple/10 nexus-border-glow"
                  : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`plan-${p.tier}`}
              >
                {p.best && <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest bg-nexus-pink text-white px-2 py-0.5 rounded-full font-semibold">Popular</div>}
                {p.founding && <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-widest bg-yellow-500 text-black px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><Sparkles className="h-3 w-3" /> Founding</div>}
                <div className="text-xs uppercase tracking-widest text-zinc-400">{p.name}</div>
                <div className="mt-2 font-display font-bold text-4xl tracking-tighter">{p.price}<span className="text-sm text-zinc-500">{p.cycle}</span></div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.perks.map((perk, i) => (
                    <li key={i} className="text-sm text-zinc-300 flex items-start gap-2"><Check className="h-4 w-4 text-nexus-blue shrink-0 mt-0.5" /> {perk}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => subscribe(p.key)}
                  disabled={isCurrent || busy === p.key || isFree}
                  className={`mt-6 w-full rounded-full font-semibold border-0 ${
                    p.founding ? "bg-yellow-500 text-black hover:bg-yellow-400"
                    : p.best ? "nexus-gradient text-white hover:opacity-90"
                    : "bg-white/5 text-white hover:bg-white/10"
                  }`}
                  data-testid={`plan-subscribe-${p.tier}`}
                >
                  {isCurrent ? "Current plan" : busy === p.key ? "Redirecting…" : isFree ? "Free forever" : p.founding ? "Claim founding" : `Upgrade to ${p.name}`}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Referral card */}
        {ref && (
          <div className="glass rounded-2xl p-6" data-testid="referral-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">🎁 Referral rewards</div>
                <h2 className="font-display font-bold text-2xl tracking-tighter">Refer a brand · get one free month</h2>
                <p className="text-zinc-400 text-sm mt-1.5">Every brand that upgrades via your link earns you one free month of Starter (£29).</p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Referred</div>
                  <div className="font-display font-bold text-2xl tabular-nums" data-testid="ref-referred">{ref.referred_count}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Upgraded</div>
                  <div className="font-display font-bold text-2xl tabular-nums text-emerald-300" data-testid="ref-upgraded">{ref.upgraded_count}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Free months</div>
                  <div className="font-display font-bold text-2xl tabular-nums nexus-gradient-text" data-testid="ref-months">{ref.reward_months_granted}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <code className="px-3 py-2 rounded-lg bg-white/[0.03] hairline text-sm text-zinc-200 font-mono flex-1 min-w-[240px] truncate" data-testid="ref-link">
                {`${window.location.origin}/auth?mode=signup&ref=${ref.code}`}
              </code>
              <Button onClick={copyRefLink} variant="outline" className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white" data-testid="ref-copy-btn">
                {copied ? "Copied ✓" : "Copy link"}
              </Button>
              {ref.reward_months_available > 0 && (
                <Button onClick={redeem} disabled={redeemBusy} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="ref-redeem-btn">
                  {redeemBusy ? "Redeeming…" : `Redeem ${ref.reward_months_available} free month${ref.reward_months_available > 1 ? "s" : ""}`}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {board && board.leaderboard && board.leaderboard.length > 0 && (
          <div className="glass rounded-2xl p-6" data-testid="referral-leaderboard">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">🏆 This month&apos;s top referrers</div>
                <h2 className="font-display font-bold text-2xl tracking-tighter">The Nexus Circle</h2>
                <p className="text-xs text-zinc-500 mt-1">Top {board.leaderboard.length} · resets {board.period}</p>
              </div>
            </div>
            <div className="space-y-2">
              {board.leaderboard.map((row) => {
                const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : `#${row.rank}`;
                const isMe = ref && row.user_id === (sub?.user_id || "");
                return (
                  <div key={row.user_id} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${row.rank <= 3 ? "bg-gradient-to-r from-yellow-500/[0.06] to-transparent hairline" : "bg-white/[0.02] hairline"} ${isMe ? "border-nexus-purple/40" : ""}`} data-testid={`leaderboard-row-${row.rank}`}>
                    <div className="w-10 shrink-0 text-lg font-display font-bold text-center">{medal}</div>
                    {row.avatar_url ? (
                      <img src={row.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-white/[0.05] flex items-center justify-center text-xs font-display font-bold shrink-0">{(row.name || "?").charAt(0)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate">{row.name}</div>
                      {row.company && <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{row.company}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-bold text-xl tabular-nums nexus-gradient-text">{row.upgraded_count}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">upgrades</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-xs text-zinc-500">Secure checkout via Stripe. Cancel anytime.</p>
      </div>
    </BrandShell>
  );
}

/* ================= NOTIFICATIONS ================= */
export function BrandNotifications() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/brand/dashboard-stats").then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <BrandShell title="Notifications">
      <div className="p-6 lg:p-10 space-y-6">
        <h1 className="font-display font-bold text-3xl tracking-tighter">Notifications</h1>
        <div className="glass rounded-2xl p-6" data-testid="brand-notifications">
          {!data ? <div className="text-sm text-zinc-500 animate-pulse">Loading…</div> :
            (data.activity || []).length === 0 ? <div className="text-sm text-zinc-500">You&apos;re all caught up.</div> :
              (data.activity || []).map((a, i) => (
                <Link key={i} to={a.link} className="flex items-center gap-3 py-3 border-b border-white/5 last:border-b-0 hover:text-white transition-colors">
                  <Bell className="h-4 w-4 text-nexus-pink shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-200 truncate">{a.text}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{timeAgo(a.when)}</div>
                  </div>
                </Link>
              ))
          }
        </div>
      </div>
    </BrandShell>
  );
}

/* ================= SETTINGS ================= */
export function BrandSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hideLb, setHideLb] = useState(false);
  const [savingPref, setSavingPref] = useState(false);
  useEffect(() => {
    api.get("/brand/settings").then((r) => setForm({ company: r.data.company || "", website: r.data.website || "", industry: r.data.industry || "", logo_url: r.data.logo_url || "", contact_email: r.data.contact_email || "" })).catch(() => {});
    api.get("/me/preferences").then((r) => setHideLb(!!r.data.hide_from_leaderboard)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/brand/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggleHide = async (next) => {
    setSavingPref(true);
    setHideLb(next);
    try { await api.patch("/me/preferences", { hide_from_leaderboard: next }); toast.success(next ? "You'll be shown as Anonymous" : "You're back on the leaderboard"); }
    catch { setHideLb(!next); toast.error("Could not save"); }
    finally { setSavingPref(false); }
  };
  if (!form) return <BrandShell title="Settings"><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></BrandShell>;
  return (
    <BrandShell title="Settings">
      <div className="p-6 lg:p-10 max-w-3xl space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Settings</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Company profile</h1>
        </div>
        <div className="glass rounded-2xl p-6 space-y-4" data-testid="brand-settings-form">
          {[
            { k: "company", label: "Company name", icon: Building2 },
            { k: "website", label: "Website", icon: ExternalLink, placeholder: "https://…" },
            { k: "industry", label: "Industry", icon: PenSquare, placeholder: "Fashion, Fitness, SaaS…" },
            { k: "logo_url", label: "Logo URL", icon: ExternalLink, placeholder: "https://…" },
            { k: "contact_email", label: "Contact email", icon: MessageSquare, placeholder: "team@brand.com" },
          ].map((f) => (
            <div key={f.k}>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">{f.label}</Label>
              <Input value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.placeholder || ""} className="mt-1.5 bg-white/[0.02] border-white/10 text-white" data-testid={`settings-${f.k}`} />
            </div>
          ))}
          <Button onClick={save} disabled={saving} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="brand-privacy-block">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Privacy</div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold text-sm">Anonymise me on the referral leaderboard</div>
              <p className="text-xs text-zinc-500 mt-0.5">Public leaderboard visitors will see &ldquo;Anonymous brand&rdquo; instead of your name.</p>
            </div>
            <button
              type="button"
              onClick={() => toggleHide(!hideLb)}
              disabled={savingPref}
              data-testid="settings-hide-leaderboard"
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${hideLb ? "bg-nexus-purple" : "bg-white/10"} disabled:opacity-50`}
              aria-pressed={hideLb}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hideLb ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>
        <ShopifyConnect />
      </div>
    </BrandShell>
  );
}
